import * as Sentry from '@sentry/react'

const STORAGE_KEY = 'desres_custom_categories'

export const KITCHEN_CATEGORIES_SEED = [
  { name: 'mala', label: 'Mala', openingUnit: 'rands', surplusUnit: 'rands', salesUnit: 'rands', projectable: true, type: 'continuous_variable' },
  { name: 'chicken liver', label: 'Chicken Liver', openingUnit: 'rands', surplusUnit: 'rands', salesUnit: 'rands', projectable: true, type: 'continuous_variable' },
  { name: 'mashondzha', label: 'Mashondzha', openingUnit: 'rands', surplusUnit: 'rands', salesUnit: 'rands', projectable: true, type: 'continuous_variable' },
  { name: 'pap only', label: 'Pap Only', openingUnit: 'kg', surplusUnit: 'kg', salesUnit: 'rands', projectable: false, type: 'continuous_variable' },
  { name: 'mutuku', label: 'Mutuku', openingUnit: 'units', surplusUnit: 'units', salesUnit: 'rands', projectable: true, type: 'fixed_unit', unitPrice: 10 },
  { name: 'makhaha', label: 'Makhaha', openingUnit: 'units', surplusUnit: 'units', salesUnit: 'rands', projectable: true, type: 'fixed_unit', unitPrice: 10 },
  { name: 'khwanda', label: 'Khwanda', openingUnit: 'units', surplusUnit: 'units', salesUnit: 'rands', projectable: true, type: 'discrete' },
  { name: 'mukokoroshi', label: 'Mukokoroshi', openingUnit: 'units', surplusUnit: 'units', salesUnit: 'rands', projectable: true, type: 'discrete' },
  { name: 'steak', label: 'Steak', openingUnit: 'rands', surplusUnit: 'rands', salesUnit: 'rands', projectable: true, type: 'continuous_variable' },
  { name: 'stew vors', label: 'Stew Vors', openingUnit: 'rands', surplusUnit: 'rands', salesUnit: 'rands', projectable: true, type: 'continuous_variable' },
  { name: 'vors', label: 'Vors', openingUnit: 'rands', surplusUnit: 'rands', salesUnit: 'rands', projectable: true, type: 'continuous_variable' },
  { name: 'magwanda', label: 'Gwanda La Kholomo', openingUnit: 'rands', surplusUnit: 'rands', salesUnit: 'rands', projectable: true, type: 'continuous_variable' },
  { name: 'muroho', label: 'Muroho', openingUnit: 'units', surplusUnit: 'units', salesUnit: 'rands', projectable: true, type: 'fixed_unit', unitPrice: 12 },
  { name: 'russian', label: 'Russian', openingUnit: 'units', surplusUnit: 'units', salesUnit: 'rands', projectable: true, type: 'fixed_unit', unitPrice: 8 },
  { name: 'salad', label: 'Salad', openingUnit: 'units', surplusUnit: 'units', salesUnit: 'rands', projectable: true, type: 'fixed_unit', unitPrice: 10 },
]

export const UNIT_LABELS = {
  rands: 'R',
  units: 'units',
  kg: 'kg',
}

export const UNIT_OPTIONS = [
  { value: 'rands', label: 'Rands (R)' },
  { value: 'units', label: 'Units' },
  { value: 'kg', label: 'Kilograms (kg)' },
]

export const CATEGORY_TYPES = [
  { value: 'continuous_variable', label: 'Continuous Variable' },
  { value: 'discrete', label: 'Discrete' },
  { value: 'fixed_unit', label: 'Fixed Unit Price' },
]

// --- Dynamic category management ---

function loadCustomCategories() {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    return data ? JSON.parse(data) : []
  } catch { return [] }
}

function saveCustomCategories(categories) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(categories))
  // Sync to Firestore
  syncCategoriesToFirestore(categories)
}

async function syncCategoriesToFirestore(categories) {
  try {
    const { db } = await import('../lib/firebase')
    const { doc, setDoc } = await import('firebase/firestore')
    for (const c of categories) {
      const id = c.name.toLowerCase().replace(/\s+/g, '_')
      await setDoc(doc(db, 'categories', id), { ...c, id }).catch(err => {
        console.warn('[categories] syncCategoriesToFirestore setDoc failed:', err?.message)
        Sentry.captureException(err, { extra: { module: 'categories', fn: 'syncCategoriesToFirestore.setDoc', id } })
      })
    }
  } catch (err) {
    console.warn('[categories] syncCategoriesToFirestore import failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'categories', fn: 'syncCategoriesToFirestore.import' } })
  }
}

// Get all categories (seed + custom, with overrides)
export function getCategories() {
  const custom = loadCustomCategories()
  const customNames = new Set(custom.map(c => c.name.toLowerCase()))
  const filtered = KITCHEN_CATEGORIES_SEED.filter(c => !customNames.has(c.name.toLowerCase()))
  return [...filtered, ...custom].filter(c => !c.deleted)
}

// Backward compat
export const KITCHEN_CATEGORIES = new Proxy([], {
  get(target, prop) {
    const cats = getCategories()
    if (prop === Symbol.iterator) return cats[Symbol.iterator].bind(cats)
    if (prop === 'length') return cats.length
    if (typeof prop === 'string' && !isNaN(prop)) return cats[Number(prop)]
    if (typeof cats[prop] === 'function') return cats[prop].bind(cats)
    return cats[prop]
  }
})

// Add a new category
export function addCategory(category) {
  const custom = loadCustomCategories()
  const existing = custom.findIndex(c => c.name.toLowerCase() === category.name.toLowerCase())
  const record = { ...category, custom: true, createdAt: new Date().toISOString() }
  if (existing >= 0) {
    custom[existing] = record
  } else {
    custom.push(record)
  }
  saveCustomCategories(custom)
  return record
}

// Update a category (works for both seed overrides and custom)
export function updateCategory(name, updates) {
  const custom = loadCustomCategories()
  const existing = custom.findIndex(c => c.name.toLowerCase() === name.toLowerCase())

  if (existing >= 0) {
    custom[existing] = { ...custom[existing], ...updates }
    saveCustomCategories(custom)
    return custom[existing]
  }

  // Override a seed category
  const seed = KITCHEN_CATEGORIES_SEED.find(c => c.name.toLowerCase() === name.toLowerCase())
  if (seed) {
    const override = { ...seed, ...updates, custom: true, createdAt: new Date().toISOString() }
    custom.push(override)
    saveCustomCategories(custom)
    return override
  }

  return null
}

// Delete a category
export function deleteCategory(name) {
  const custom = loadCustomCategories()
  const idx = custom.findIndex(c => c.name.toLowerCase() === name.toLowerCase())

  if (idx >= 0) {
    custom.splice(idx, 1)
    saveCustomCategories(custom)
    return true
  }

  // For seed categories, add a deleted override
  const seed = KITCHEN_CATEGORIES_SEED.find(c => c.name.toLowerCase() === name.toLowerCase())
  if (seed) {
    custom.push({ ...seed, custom: true, deleted: true, deletedAt: new Date().toISOString() })
    saveCustomCategories(custom)
    return true
  }

  return false
}

export function getCategoryConfig(categoryName) {
  return getCategories().find(c => c.name.toLowerCase() === categoryName.toLowerCase())
}
